import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetInfoTypeNamespaces } from '@/service-library/hooks/info-type-namespaces'
import { useGetInfoTypes } from '@/service-library/hooks/info-types'
import { useGetProjectGridFieldTypes } from '@/service-library/hooks/project-grid-field-types'
import { useCreateProjectGridFieldWithDefaults } from '@/service-library/hooks/project-grid-fields'
import queryKeys from '@/service-library/query-keys'
import { ProjectGridField, ProjectGridFieldType } from '@/types/fields'
import { InfoType, InfoTypeNamespace } from '@/types/info-types'
import PaginatedResponse from '@/types/paginated-response'
import { ProjectGrid } from '@/types/projects'
import getDataTypeIcon from '@/utils/data-type'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { notNull } from '@/utils/typescript'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import ListAutocomplete from '@/components/list-autocomplete/ListAutocomplete'
import { useProjectGridsContext } from '@/components/project-settings/ProjectGridsProvider'
import useGetNamespaceOptions from './useGetNamespaceOptions'

type OptionNamespace = {
  id: string
  name: string
  group: string
}

type CreateFieldDialogProps = {
  overlay: OverlayState
  isCreatingTable: boolean
  projectGrid: ProjectGrid
  onCustomFieldClick: () => void
}

type HandleAddFieldOptions = {
  fieldType: ProjectGridFieldType
  infoType?: InfoType
  namespace?: InfoTypeNamespace
}

type InfoTypesListItemsProps = {
  infoTypes: InfoType[]
  fieldTypesMap: Record<string, ProjectGridFieldType>
  infoTypeIdsInUse: string[]
  handleAddField: (handleAddFieldOptions: HandleAddFieldOptions) => void
}

type NamespacesListItemsProps = {
  infoTypeNamespaces: InfoTypeNamespace[]
  onCreateTableField: (namespace: InfoTypeNamespace) => void
}

function formatValidName(name: string, fields: ProjectGridField[]) {
  let newName = name
  let counter = 1
  while (fields.find((field) => field.name === newName)) {
    newName = `${name} (${counter})`
    counter++
  }
  return newName
}

function InfoTypesListItems({
  infoTypes,
  fieldTypesMap,
  infoTypeIdsInUse,
  handleAddField,
}: InfoTypesListItemsProps) {
  return (
    <>
      {infoTypes.length === 0 && (
        <ListItem sx={{ pl: 5 }}>
          <Typography color="text.secondary" variant="caption">
            No results found
          </Typography>
        </ListItem>
      )}
      {infoTypes.map((infoType) => {
        const fieldType = fieldTypesMap[infoType.preferred_field_type_id]
        // We won't show types that require params for now
        if (!fieldType || fieldType.code === 'picker') return null
        const Icon = getDataTypeIcon(fieldType.code)
        const alreadyInUse = infoTypeIdsInUse.includes(infoType.id)

        return (
          <ListItemButton
            key={infoType.id}
            sx={{ pl: 3 }}
            disabled={alreadyInUse}
            onClick={() => {
              handleAddField({ infoType, fieldType })
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>

            <ListItemText primary={infoType.name} secondary={fieldType.name} />
          </ListItemButton>
        )
      })}
    </>
  )
}

function NamespacesListItems({
  infoTypeNamespaces,
  onCreateTableField,
}: NamespacesListItemsProps) {
  return (
    <>
      {infoTypeNamespaces.length === 0 && (
        <ListItem sx={{ pl: 5 }}>
          <Typography color="text.secondary" variant="caption">
            No results found
          </Typography>
        </ListItem>
      )}
      {infoTypeNamespaces.map((namespace) => {
        const Icon = getDataTypeIcon('grid')
        return (
          <ListItemButton
            key={namespace.id}
            sx={{ pl: 3 }}
            onClick={() => {
              onCreateTableField(namespace)
            }}
          >
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={namespace.name} />
          </ListItemButton>
        )
      })}
    </>
  )
}

export default function CreateFieldDialog({
  overlay,
  isCreatingTable,
  projectGrid,
  onCustomFieldClick,
}: CreateFieldDialogProps) {
  const [searchValue, setSearchValue] = useState('')
  const deferredSearchValue = useDeferredValue(searchValue)
  const [namespaceId, setNamespaceId] = useState<string>('')

  const { projectGridFieldTypes } = useGetProjectGridFieldTypes({
    filters: {
      limit: '1000',
    },
    enabled: overlay.isOpen,
  })

  const { allNamespaces } = useGetNamespaceOptions({
    enabled: overlay.isOpen,
    projectGrid,
  })

  const namespace = allNamespaces.find(
    (namespace) => namespaceId === namespace.id,
  )

  const { infoTypes, isPending: infoTypesIsLoading } = useGetInfoTypes({
    filters: {
      limit: '1000',
      namespace_id: namespaceId,
    },
    placeholderData: (previousData) => previousData,
    enabled: overlay.isOpen && !isCreatingTable && !!namespace,
  })

  const { infoTypeNamespaces, isPending: namespacesIsLoading } =
    useGetInfoTypeNamespaces({
      filters: {
        limit: '1000',
        parent_id: namespaceId,
      },
      enabled: overlay.isOpen && isCreatingTable && !!namespace,
    })

  const { projectGrids, queryKey } = useProjectGridsContext()

  const queryClient = useQueryClient()
  const { createProjectGridField } = useCreateProjectGridFieldWithDefaults({
    sideEffectQueryKeys: [queryKey, queryKeys.projects.details()],
    onMutate: (newField) => {
      queryClient.setQueryData<InfiniteData<PaginatedResponse<ProjectGrid>>>(
        queryKey,
        (oldData) => {
          const updatedPages = oldData?.pages.map((page) => {
            return {
              ...page,
              results: page.results.map((grid) => {
                if (grid.id === newField.project_grid_id) {
                  return {
                    ...grid,
                    project_grid_fields: [
                      ...grid.project_grid_fields,
                      newField,
                    ],
                  }
                }
                return grid
              }),
            }
          })
          return {
            pageParams: oldData?.pageParams || [],
            pages: updatedPages || [],
          }
        },
      )
    },
    onSuccess: () => {
      showSuccessSnackbar('Field Added')
    },
    onError: () => {
      showErrorSnackbar('Failed to add field. Please try again later.')
    },
  })

  function handleAddField({
    fieldType,
    infoType,
    namespace,
  }: {
    fieldType: ProjectGridFieldType
    infoType?: InfoType
    namespace?: InfoTypeNamespace
  }) {
    const fieldName = formatValidName(
      infoType ? infoType.name : namespace?.name || '',
      projectGrid.project_grid_fields,
    )

    createProjectGridField({
      id: generateUuid(),
      project_grid_id: projectGrid.id,
      description: '',
      live_list_filter_config: null,
      alignment: 'left',
      project_grid_field_type_id: fieldType.id,
      required: false,
      needed: 'optional',
      has_category_model: false, // TODO: Consider adjusting this, once we have global pickers.
      name: fieldName,
      params: {}, // TODO: Once we have global pickers or live lists picker info types, we will need to adjust this
      sort_order: 1000001,
      input_behavior:
        fieldType.code === 'live-list-picker'
          ? 'manual_only'
          : 'manual_allowed',
      is_hidden: false,
      is_user_editable: true,
      metadata: {},
      project_content_category_id: null,
      sub_project_grid_id: null,
      use_region_of_interest: false,
      region_of_interest_bottom: null,
      region_of_interest_left: null,
      region_of_interest_right: null,
      region_of_interest_top: null,
      region_of_interest_page: 1,
      contextual_type: null,
      user_can_change_data_type: true,
      user_can_delete: true,
      field_group_id: null,
      project_grid_field_type: fieldType, // This is just to pass the data for the on mutate update
      category_items: undefined, // Deprecated
      fields: fieldType.code === 'grid' ? [] : undefined,
      included_grid_fields_ids: [],
      included_grid_fields: [],
      project_grid_field_rules: [],
      aggregate_function: 'none',
      info_type_id: infoType ? infoType.id : null,
      info_type: infoType,
      original_namespace_id: namespace?.id || null,
      is_lite_column: false,
      ask_on_import: 'no',
    })
  }

  const projectGridFields = projectGrids.flatMap(
    (grid) => grid.project_grid_fields,
  )

  const fieldTypesMap = useMemo(
    () =>
      projectGridFieldTypes.reduce<Record<string, ProjectGridFieldType>>(
        (acc, fieldType) => {
          acc[fieldType.id] = fieldType
          return acc
        },
        {},
      ),
    [projectGridFieldTypes],
  )

  const tableFieldType = useMemo(
    () =>
      projectGridFieldTypes.find(
        (fieldType) => fieldType.code === 'grid',
      ) as ProjectGridFieldType,
    [projectGridFieldTypes],
  )

  const infoTypesInUse = useMemo(() => {
    return projectGridFields.map((field) => field.info_type_id).filter(notNull)
  }, [projectGridFields])

  const filteredInfoTypes = useMemo(() => {
    return infoTypes.filter((infoType) =>
      infoType.name.toLowerCase().includes(deferredSearchValue.toLowerCase()),
    )
  }, [deferredSearchValue, infoTypes])

  const filteredNamespaces = useMemo(() => {
    return infoTypeNamespaces.filter((namespace) =>
      namespace.name.toLowerCase().includes(deferredSearchValue.toLowerCase()),
    )
  }, [deferredSearchValue, infoTypeNamespaces])

  const isLoading = isCreatingTable ? namespacesIsLoading : infoTypesIsLoading

  useEffect(() => {
    if (overlay.isOpen && !namespace && allNamespaces.length > 0) {
      setNamespaceId(
        projectGrid.original_namespace_id || projectGrid.namespace_id,
      )
    }
  }, [
    allNamespaces.length,
    namespace,
    overlay.isOpen,
    projectGrid.namespace_id,
    projectGrid.original_namespace_id,
  ])

  return (
    <Dialog
      {...overlay}
      title={`Add ${isCreatingTable ? 'Table' : 'Field'}`}
      maxWidth="md"
      slotProps={{
        transition: {
          onExited: () => {
            setNamespaceId('')
            setSearchValue('')
          },
        },
      }}
    >
      <Stack spacing={2} sx={{ pl: 3, pr: 2, pb: 1 }}>
        <Typography variant="body2">
          Choose a {isCreatingTable ? 'table' : 'field'} type to add. Try to be
          as specific as possible. If none of the existing{' '}
          {isCreatingTable ? 'table' : 'field'} types meet your needs, you can
          create a custom
          {isCreatingTable ? 'table' : 'field'} type.
        </Typography>

        <Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              autoFocus
              label="Search"
              value={searchValue}
              onChange={({ target }) => setSearchValue(target.value)}
              sx={{ flexGrow: 1.5 }}
              disabled={isLoading}
            />
            <Box sx={{ flexGrow: 1 }}>
              <ListAutocomplete<OptionNamespace>
                autoSelect={false}
                selected={namespace || null}
                setSelected={(namespace) => {
                  setNamespaceId(namespace.id)
                }}
                options={allNamespaces}
                groupBy={(namespace) => namespace.group}
                label="Namespace"
              />
            </Box>
          </Stack>
        </Box>
      </Stack>

      <DialogContent disablePadding={!isLoading}>
        {isLoading && (
          <Stack spacing={1}>
            {Array.from({ length: 5 }).map((_, index) => {
              return (
                <Box key={index}>
                  <Skeleton height={20} width={200} />
                  <Skeleton height={20} width={125} />
                </Box>
              )
            })}
          </Stack>
        )}
        {!isLoading && (
          <List dense disablePadding sx={{ pb: 4, mt: -1 }}>
            {isCreatingTable ? (
              <NamespacesListItems
                infoTypeNamespaces={filteredNamespaces}
                onCreateTableField={(namespace) => {
                  handleAddField({ namespace, fieldType: tableFieldType })
                  overlay.close()
                }}
              />
            ) : (
              <InfoTypesListItems
                infoTypes={filteredInfoTypes}
                fieldTypesMap={fieldTypesMap}
                handleAddField={handleAddField}
                infoTypeIdsInUse={infoTypesInUse}
              />
            )}
          </List>
        )}
      </DialogContent>

      <DialogFooter
        details={
          <Button
            startIcon={<Add />}
            onClick={onCustomFieldClick}
            variant="text"
          >
            Custom {isCreatingTable ? 'Table' : 'Field'}
          </Button>
        }
      >
        <Button onClick={overlay.close} variant="text">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
